import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useMutation, useLazyQuery } from '@apollo/client';
import {
  GET_USER_BLOCK,
  GET_SENT_EMAIL_MESSAGES,
  ADMIN_USERS_CSV,
  GET_USER_2FA,
  DELETE_USER_2FA
} from '../../../../../../api/queries/queries_local';

// Components
import { Actionbar, Flexbox, SearchBox } from '@ibinder/common-frontend';
import { ToolButton } from '@ibinder/common-frontend';
import ImpersonateUserModal from './ImpersonateUserModal';
import OrgViewModal from '../OrganizationViewer/OrgViewModal';
import CustomerViewModal from '../CustomerViewer/CustomerViewModal';
import AdminModal from '../AdminModal';
import GenericModalWrapper from '../../../../../../components/GlobalTemplates/GenericModalWrapper';

// Icons
import {
  delete as deleteFile,
  plus,
  search,
  user,
  edit,
  eyeOpened,
  switch as switchIcon,
  envelope,
  redo
} from '@ibinder/common-icons';
import { useToggleWithClickOutside } from 'Shared/hooks/useToggleWithClickOutside';
import {
  Config,
  downloadFile,
  fileTypes,
  isPromise,
  setImpersonateDataCookie
} from '@ibinder/common-utils';
import { getAppSetting } from 'Shared/ApplicationSettings';
import BinderAdminViewModal from 'components/AdminBinder/AdminContentContainer/TabContent/ListContainer/BinderViewer/BinderViewModal';
import ChangeOrganizationModal from '../BinderViewer/ChangeOrganizationModal';
import TenderAdminViewModal from '../TenderViewer/TenderViewModal';
import EmailBlockData from '../../EmailBlockData';
import EmailMessagesList from '../../EmailMessagesList';
import { MIME_TYPES } from '@ibinder/ui-common-models';
import dayjs from 'dayjs';
import ConfirmRestoreBinderModal from '../BinderViewer/ConfirmRestoreBinderModal';
import {
  useFeatureToggle,
  useIBTranslation,
  useModalAction
} from '@ibinder/ui-common-hooks';
import CreateBrandedUserInviteModal from '../Users/CreateBrandedUserInviteModal';
import UserMfaModal from '../Users/UserMfaModal';

const FETCH_LIMIT = 5;
const DATE_FORMAT = 'YYYY-MM-DD-HHmmss';

const StyledActionbar = styled(Actionbar)`
  margin-left: -1rem;
  margin-right: -1rem;
  @media (max-width: ${(p) => p.theme.devices.xs}px) {
    margin-left: -0.5rem;
    margin-right: -0.5rem;
  }
`;
const StyledSearchBox = styled(SearchBox)`
  margin: 0;
`;
const ActionbarContent = styled(Flexbox)`
  width: 100%;
  justify-content: flex-end;
  align-items: center;
  margin: 0.25rem;
`;
const StyledToolButton = styled(ToolButton)`
  div > input {
    display: none;
  }
`;

const ExtraSpace = styled.span`
  margin-right: 0.75rem;
  @media (max-width: ${(p) => p.theme.devices.xs}px) {
    display: none;
  }
`;

const ModalSubtitle = styled.h3`
  padding: 0.5rem 0;
  color: ${(p) => p.theme.colors.primary};
`;

export const compliantEnhancedURL = (url) => {
  const _compliantURL = true;
  const currURL = window.location.origin;
  let _url = url;

  if (_compliantURL) {
    switch (currURL) {
      case 'https://test-compliant-admin.ibinder.com':
        _url = 'https://test-compliant.ibinder.com/';
        break;
      case 'http://localhost:8080':
        _url = 'https://www.google.com/';
        break;

      default:
        break;
    }
  }

  return _url;
};

export const impersonateUser = (
  token,
  fullName,
  email,
  userId,
  binder = undefined,
  organizationId = undefined
) => {
  setImpersonateDataCookie(token);

  let url = compliantEnhancedURL(getAppSetting('REACT_APP_PUBLIC_URL'));

  if (organizationId) {
    url = url.replace(/\/$/, '');

    // regular binders
    if (binder?.id) {
      url += `/${organizationId}/binder/${binder.id}`;
      // old organization binders
    } else if (binder?.organizationBinderId) {
      url += `/${organizationId}/binder/${binder.organizationBinderId}`;
      // new organizations
    } else {
      url += `/organization/${organizationId}`;
    }
  }

  window.open(url);
};

export const impersonateTenderUser = (
  token,
  fullName,
  email,
  userId,
  tender = undefined,
  organizationId = undefined,
  bidId = undefined
) => {
  setImpersonateDataCookie(token);
  let url = compliantEnhancedURL(getAppSetting('REACT_APP_PUBLIC_URL'));

  if (tender && organizationId) {
    url = url.replace(/\/$/, '');
    url += bidId
      ? `/etendering/${organizationId}/tenders/${tender.id}?bidId=${bidId}`
      : `/etendering/${organizationId}/tenders/${tender.id}`;
  }

  window.open(url);
};

const AdminActionBar = ({
  loading,
  refreshOrgList,
  refreshCustomerList,
  setSelectedIds,
  selectedFilesDocumentId: selectedDocumentIds,
  setSearchQuery,
  searchQuery,
  type,
  data,
  onViewUserAccess = () => null,
  refetchList = () => null
}) => {
  const { t } = useIBTranslation();

  const { isEnabled: showMoveBinderInAdminUi } = useFeatureToggle(
    'moveBinderInAdminUi',
    Config.OPERATIONAL_CONFIG
  );

  const node = useRef();
  const [impersonatedUser, setImpersonatedUser] = useState(null);
  const { isOpen: isBlocksModalOpen, toggle: toggleBlocksModal } =
    useToggleWithClickOutside(node);
  const [getUserBlocks, { loading: loadingUserBlock, data: userBlock }] =
    useLazyQuery(GET_USER_BLOCK, {
      fetchPolicy: 'no-cache'
    });

  const [
    getSentEmailMessages,
    { loading: loadingSentEmailMessages, data: sentEmailMessages }
  ] = useLazyQuery(GET_SENT_EMAIL_MESSAGES, {
    fetchPolicy: 'no-cache'
  });

  const [getUser2FA, { loading: loadingMfa }] = useLazyQuery(GET_USER_2FA, {
    fetchPolicy: 'no-cache'
  });
  const [mfaLevel, setMfaLevel] = useState<
    'required' | 'optional' | 'off' | null
  >(null);

  const [deleteUser2FA, { loading: removingMfa }] =
    useMutation(DELETE_USER_2FA);
  const [mfaAccessDenied, setMfaAccessDenied] = useState(false);

  const userId = localStorage.getItem('userId');
  const [getAdminUsersAsCSV, adminUsersAsCSV] = useLazyQuery(ADMIN_USERS_CSV, {
    context: {
      headers: { 'x-ibinder-tenantid': userId, accept: MIME_TYPES.CSV }
    },
    fetchPolicy: 'no-cache'
  });

  const [showOrganizationData, setShowOrganizationData] = useState([]);
  const [showCustomerData, setShowCustomerData] = useState([]);
  const [changeOrganization, setChangeOrganization] = useState([]);
  const [showCreateBrandedInvite, setShowCreateBrandedInvite] = useState(false);
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [showCreateCustomer, setShowCreateCustomer] = useState(false);
  const [redirectFromCreateOrg, setRedirectFromCreateOrg] = useState(false);
  const [showMfaModal, setShowMfaModal] = useState(false);

  const handleSetOrganization = (orgId?: string) => {
    if (orgId) {
      setShowOrganizationData([orgId]);
      setSelectedIds([orgId]);
    } else {
      setShowOrganizationData([]);
      setSelectedIds([]);
    }
  };

  const handleSetCustomer = (customerId?: string) => {
    if (customerId) {
      setShowCustomerData([customerId]);
      setSelectedIds([customerId]);
    } else {
      setShowCustomerData([]);
      setSelectedIds([]);
    }
  };

  const onCloseBinderViewer = () => {
    setShowOrganizationData([]);
  };

  const onCloseOrgViewer = () => {
    handleSetOrganization();
    setShowCreateOrg(false);
    setRedirectFromCreateOrg(false);
    refreshOrgList();
  };

  const onCloseCustomerViewer = () => {
    handleSetCustomer();
    setShowCreateCustomer(false);
    refreshCustomerList?.();
  };

  const onCloseChangeOrganization = () => {
    setChangeOrganization([]);
  };

  const createOrganization = () => {
    setShowCreateOrg(true);
    setRedirectFromCreateOrg(true);
  };

  const createCustomer = () => {
    setShowCreateCustomer(true);
  };

  const getSelectedInfo = () => {
    return data.find((item) => item.id === selectedDocumentIds[0]);
  };

  const exportAsCSV = () => {
    getAdminUsersAsCSV({
      variables: {
        filterQuery: searchQuery
      }
    }).then((adminUsersAsCSV) => {
      const CSV_Content = adminUsersAsCSV?.data?.adminUsersCSV;
      const date = dayjs().format(DATE_FORMAT);
      if (CSV_Content) {
        downloadFile(`AdminUsersCSV_${date}`, CSV_Content, fileTypes.CSV);
      }
    });
  };

  const TOOLS_USERS = {
    selected: {
      blocks: {
        name: 'blocks',
        text: t('Check mail'),
        handler: async () => {
          const email = getSelectedInfo().email;
          if (email) {
            const encodedEmail = encodeURIComponent(email);
            await getUserBlocks({ variables: { email: encodedEmail } });
            await getSentEmailMessages({
              variables: { email: encodedEmail, nrOfMessages: FETCH_LIMIT }
            });
            toggleBlocksModal();
          }
        }
      },
      impersonate: {
        name: 'impersonate',
        text: t('impersonate'),
        handler: () => setImpersonatedUser(getSelectedInfo())
      },
      checkMfa: {
        name: 'checkMfa',
        text: t('Check MFA'),
        handler: async () => {
          const userId = getSelectedInfo().id;
          if (userId) {
            setMfaAccessDenied(false);
            setMfaLevel(null);
            setShowMfaModal(true);
            const result = await getUser2FA({ variables: { userId } });
            const level = result?.data?.get?.level;
            setMfaLevel(
              level === 'required' || level === 'optional' ? level : 'off'
            );
          }
        }
      },
      viewAccess: {
        name: 'viewAccess',
        text: t('View access'),
        handler: () => onViewUserAccess(getSelectedInfo())
      }
    },
    unselected: {
      create: {
        text: t('openCreateBrandedInvite'),
        handler: () => setShowCreateBrandedInvite(true)
      },
      exportCSV: {
        text: t('export CSV'),
        handler: () => exportAsCSV()
      }
    }
  };
  const TOOLS_ORGS = (noOfOrgsSelected = 0) => {
    const result = {
      selected: {},
      unselected: {
        createOrg: {
          text: t('createOrg'),
          handler: createOrganization
        }
      }
    };

    if (noOfOrgsSelected === 1) {
      result.selected['view'] = {
        text: t('view'),
        handler: () => setShowOrganizationData(selectedDocumentIds)
      };
    }

    return result;
  };

  const TOOLS_CUSTOMERS = (noOfCustomersSelected = 0) => {
    const result = {
      selected: {},
      unselected: {
        createCustomer: {
          text: t('Add Customer'),
          handler: createCustomer
        }
      }
    };

    if (noOfCustomersSelected === 1) {
      result.selected['view'] = {
        text: t('view'),
        handler: () => setShowCustomerData(selectedDocumentIds)
      };
    }

    return result;
  };

  const TOOLS_BINDERS =
    selectedDocumentIds.length > 1
      ? {
          selected: {
            view: {
              text: t('view'),
              handler: () => setShowOrganizationData(selectedDocumentIds)
            }
          },
          unselected: {}
        }
      : {
          selected: {
            view: {
              text: t('view'),
              handler: () => setShowOrganizationData(selectedDocumentIds)
            }
          },
          unselected: {}
        };

  const TOOLS_DELETED_BINDERS = {
    selected: {
      view: {
        text: t('view'),
        handler: () => setShowOrganizationData(selectedDocumentIds)
      }
    },
    unselected: {}
  };

  const TOOLS_TENDERS =
    selectedDocumentIds.length > 1
      ? {
          selected: {
            view: {
              text: t('view'),
              handler: () => setShowOrganizationData(selectedDocumentIds)
            }
          },
          unselected: {}
        }
      : {
          selected: {
            view: {
              text: t('view'),
              handler: () => setShowOrganizationData(selectedDocumentIds)
            }
          },
          unselected: {}
        };

  if (selectedDocumentIds.length === 1) {
    //@ts-ignore
    if (showMoveBinderInAdminUi) {
      TOOLS_BINDERS.selected['move'] = {
        text: t('changeOrganization'),
        handler: () => setChangeOrganization(selectedDocumentIds)
      };
    }
    TOOLS_DELETED_BINDERS.selected['restore'] = {
      text: 'Restore',
      name: 'restore',
      handler: () =>
        onOpenRestoreBinderModal(
          data.find((d) => d.binder.id === selectedDocumentIds[0])
        )
    };
  }

  const ACTIONBAR_ICONS = {
    delete: deleteFile,
    impersonate: user,
    createOrg: plus,
    createCustomer: plus,
    create: plus,
    edit: edit,
    view: eyeOpened,
    changeOrganization: switchIcon,
    blocks: envelope,
    restore: redo,
    checkMfa: eyeOpened,
    viewAccess: eyeOpened
  };

  interface ToolbarType {
    name?: string;
    text: string;
    handler: () => void;
  }

  const getToolsOfType = () => {
    switch (type) {
      case 'Users':
        return TOOLS_USERS;
      case 'Organizations':
        return TOOLS_ORGS(selectedDocumentIds.length);
      case 'Customers':
        return TOOLS_CUSTOMERS(selectedDocumentIds.length);
      case 'Binders':
        return TOOLS_BINDERS;
      case 'DeletedBinders':
        // TODO: Use when backend is ready
        // case 'DeletedBindersEU':
        return TOOLS_DELETED_BINDERS;
      case 'Tenders':
        // TODO: Use when backend is ready
        // case 'TendersEU':
        return TOOLS_TENDERS;
      case 'BinderTemplateCreate':
        return TOOLS_ORGS();
    }
  };
  const { isEnabled: PackagesAndFeaturesForOrganizationInAdmin } =
    useFeatureToggle(
      'showPackagesAndFeaturesForOrganizationInAdmin',
      Config.OPERATIONAL_CONFIG
    );
  const { isEnabled: Configcat_selectPackageOnOrgCreation } = useFeatureToggle(
    'Configcat_selectPackageOnOrgCreation'
  );
  const findOrgId =
    type === 'Binders' &&
    data.filter((d) => d.binder?.id === selectedDocumentIds[0]);
  const areFilesSelected = selectedDocumentIds.length > 0;

  const [RestoreBinderModal, onOpenRestoreBinderModal] = useModalAction(
    null,
    ({ onClose, data: selected }) => (
      <ConfirmRestoreBinderModal
        data={selected}
        onAfterClose={refetchList}
        onClose={onClose}
      />
    )
  );

  return (
    <StyledActionbar
      loading={loading}
      active={areFilesSelected}
      makeSticky={false}
    >
      {impersonatedUser && (
        <ImpersonateUserModal
          targetUserId={impersonatedUser.id}
          userName={impersonatedUser.fullName}
          onClose={() => setImpersonatedUser(null)}
          onImpersonated={(token) =>
            impersonateUser(
              token,
              impersonatedUser.fullName,
              impersonatedUser.email,
              impersonatedUser.id
            )
          }
        />
      )}
      {isBlocksModalOpen && (
        <AdminModal
          ref={node}
          onClose={() => toggleBlocksModal()}
          label={`Blocks and messages for user ${getSelectedInfo().fullName}`}
          name={'User emailblocks viewer'}
          Component={() => (
            <GenericModalWrapper>
              <EmailBlockData
                userBlock={userBlock}
                loading={loadingUserBlock}
              />
              {!sentEmailMessages || !sentEmailMessages?.get?.length ? (
                <ModalSubtitle>{'No messages found'}</ModalSubtitle>
              ) : (
                <ModalSubtitle>
                  {`The last ${
                    sentEmailMessages?.get?.length < FETCH_LIMIT
                      ? sentEmailMessages?.get?.length
                      : FETCH_LIMIT
                  } messages sent to the user`}
                </ModalSubtitle>
              )}

              <EmailMessagesList
                sentEmailMessages={sentEmailMessages}
                loading={loadingSentEmailMessages}
              />
            </GenericModalWrapper>
          )}
        ></AdminModal>
      )}

      {showMfaModal && (
        <UserMfaModal
          ref={node}
          userName={getSelectedInfo()?.fullName ?? ''}
          mfaLevel={mfaLevel ?? 'off'}
          loading={loadingMfa || mfaLevel === null}
          accessDenied={mfaAccessDenied}
          onClose={() => setShowMfaModal(false)}
          onRemove2FA={() =>
            deleteUser2FA({
              variables: { userId: getSelectedInfo().id }
            })
              .then(() => setShowMfaModal(false))
              .catch((e) => {
                if (e?.networkError?.statusCode === 403) {
                  setMfaAccessDenied(true);
                }
              })
          }
          removingMfa={removingMfa}
        />
      )}

      {(showCreateOrg ||
        (showOrganizationData.length > 0 && type === 'Organizations')) && (
        <OrgViewModal
          {...{
            PackagesAndFeaturesForOrganizationInAdmin,
            Configcat_selectPackageOnOrgCreation
          }}
          onClose={onCloseOrgViewer}
          orgId={showOrganizationData[0]}
          type={type}
          isCreateOrg={showCreateOrg}
          setIsCreateOrg={setShowCreateOrg}
          handleSetOrganization={handleSetOrganization}
          redirectFromCreateOrg={redirectFromCreateOrg}
        />
      )}
      {(showCreateCustomer ||
        (showCustomerData.length > 0 && type === 'Customers')) && (
        <CustomerViewModal
          onClose={onCloseCustomerViewer}
          customerId={showCustomerData[0]}
          type={type}
          isCreateCustomer={showCreateCustomer}
          setIsCreateCustomer={setShowCreateCustomer}
          handleSetCustomer={handleSetCustomer}
        />
      )}
      {showOrganizationData.length > 0 &&
        [
          'Binders',
          'DeletedBinders'
          // TODO: Use when backend is ready
          //'DeletedBindersEU'
        ].includes(type) && (
          <BinderAdminViewModal
            onClose={onCloseBinderViewer}
            loading={false}
            selectedIds={showOrganizationData}
            selectedInfo={getSelectedInfo()}
            data={data}
            type={type}
          />
        )}
      {showOrganizationData.length > 0 && type === 'Tenders' && (
        // TODO: Use when backend is ready
        //  || type === 'TendersEU'
        <TenderAdminViewModal
          onClose={onCloseBinderViewer}
          selectedIds={showOrganizationData}
          selectedInfo={getSelectedInfo()}
          type={type}
          data={data}
        />
      )}
      {changeOrganization.length > 0 &&
        showMoveBinderInAdminUi &&
        type === 'Binders' && (
          <ChangeOrganizationModal
            data={data}
            onClose={onCloseChangeOrganization}
            selectedIds={changeOrganization}
            orgId={findOrgId[0]?.organizationId}
          />
        )}
      {showCreateBrandedInvite && (
        <CreateBrandedUserInviteModal
          onClose={() => setShowCreateBrandedInvite(false)}
        />
      )}
      {RestoreBinderModal}
      <ActionbarContent>
        {/* WHEN NO OBJECTS ARE SELECTED */}
        {areFilesSelected && (
          <>
            {Object.entries(getToolsOfType().selected).map(
              (item: [string, ToolbarType], key) => (
                <StyledToolButton
                  {...{ key }}
                  type="button"
                  onClick={item[1].handler}
                >
                  {item[1]?.name
                    ? ACTIONBAR_ICONS[item[1]?.name]
                    : ACTIONBAR_ICONS[item[1].text]}
                  <ExtraSpace>{item[1].text}</ExtraSpace>
                </StyledToolButton>
              )
            )}
          </>
        )}
        {/* WHEN OBJECTS ARE SELECTED */}
        {!areFilesSelected && (
          <>
            {Object.entries(getToolsOfType().unselected).map((item, key) => (
              <ToolButton {...{ key }} type="button" onClick={item[1].handler}>
                {plus}
                <ExtraSpace>{item[1].text}</ExtraSpace>
              </ToolButton>
            ))}
            <StyledSearchBox
              withButton={true}
              btnIcon={search}
              value={searchQuery}
              placeholder="Search..."
              onSearch={(e) => setSearchQuery(e)}
              onValueChange={(e) => setSearchQuery(e)}
            />
          </>
        )}
      </ActionbarContent>
    </StyledActionbar>
  );
};

export default AdminActionBar;
